import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Layout } from './components/Layout';
import { FishingNavigation } from './components/FishingNavigation';
import FishingLogPanel from './components/FishingLogPanel';
import Toast from './components/Toast';
import WelcomeScreen from './components/WelcomeScreen';
import Tutorial from './components/Tutorial';
import { CatchesPage } from './pages/CatchesPage';
import { CatchDetailPage } from './pages/CatchDetailPage';
import { SessionsPage } from './pages/SessionsPage';
import { AreasPage } from './pages/AreasPage';
import { StatsPage } from './pages/StatsPage';
import { QuickCatchModal } from './components/QuickCatchModal';
import WeatherApp from './WeatherApp';
import { initializeDatabase } from './services/FishingDB';

function App() {
  const [showQuickCatch, setShowQuickCatch] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'info' | 'success' | 'error' | 'warning'>('info');
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Initialize database ONCE with all default data
  useEffect(() => {
    // Check if user has seen welcome screen before
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    console.log('🔍 Initialization - hasSeenWelcome from localStorage:', hasSeenWelcome);
    
    initializeDatabase()
      .then(() => {
        console.log('✅ Database ready!');
        setIsInitializing(false);
        
        // Show welcome screen only if not seen before
        if (!hasSeenWelcome) {
          console.log('👋 First time user - showing welcome screen');
          setShowWelcome(true);
        } else {
          console.log('✅ Returning user - skipping welcome screen');
        }
      })
      .catch(error => {
        console.error('Failed to initialize database:', error);
        setIsInitializing(false); // Show UI anyway to avoid infinite loading
        
        // Show welcome screen only if not seen before
        if (!hasSeenWelcome) {
          console.log('👋 First time user (error path) - showing welcome screen');
          setShowWelcome(true);
        }
      });
  }, []);
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('App state:', { showQuickCatch, showWelcome, showTutorial, tutorialStep });
  }, [showQuickCatch, showWelcome, showTutorial, tutorialStep]);
  
  const handleWelcomeComplete = useCallback(() => {
    console.log('🎉 handleWelcomeComplete called');
    console.log('Before setState - showWelcome:', showWelcome);
    
    localStorage.setItem('hasSeenWelcome', 'true');
    console.log('localStorage hasSeenWelcome set to:', localStorage.getItem('hasSeenWelcome'));
    
    setShowWelcome(false);
    console.log('After setShowWelcome(false)');
    
    // Visa tutorial efter welcome screen
    setShowTutorial(true);
    console.log('After setShowTutorial(true)');
  }, [showWelcome]);
  
  const showToast = (message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    setToastMessage(message);
    setToastType(type);
    // Clear toast after delay
    setTimeout(() => setToastMessage(''), type === 'error' ? 4000 : 3000);
  };


  console.log('🎨 Rendering App - showWelcome:', showWelcome, 'showTutorial:', showTutorial);

  // Show welcome screen OUTSIDE of BrowserRouter to avoid routing conflicts
  if (showWelcome) {
    return <WelcomeScreen onStart={handleWelcomeComplete} />;
  }

  return (
    <BrowserRouter>
      <Layout>
        {/* Tutorial (first time after welcome) */}
        {showTutorial && (
          <Tutorial 
            onComplete={() => setShowTutorial(false)} 
            onStepChange={(step) => setTutorialStep(step)}
          />
        )}
        
        {/* Main content */}
        <Routes>
            <Route path="/" element={<WeatherApp />} />
            <Route path="/catches" element={<CatchesPage />} />
            <Route path="/catches/:catchId" element={<CatchDetailPage />} />
            <Route path="/sessions" element={<SessionsPage />} />
            <Route path="/areas" element={<AreasPage />} />
            <Route path="/stats" element={<StatsPage />} />
          </Routes>

          {/* Footer Navigation - dold under tutorial förutom sista steget */}
          {(!showTutorial || tutorialStep === 10) && (
            <FishingNavigation onQuickCatch={() => {
              console.log('FAB clicked! Opening quick catch modal...');
              setShowQuickCatch(true);
            }} />
          )}

          {/* Active session panel (when active) */}
          <FishingLogPanel />

          {/* Quick catch modal */}
          <QuickCatchModal 
            isOpen={showQuickCatch} 
            onClose={() => setShowQuickCatch(false)} 
            onSuccess={showToast}
          />
          
          {/* Toast notifications */}
          {toastMessage && (
            <Toast 
              message={toastMessage} 
              type={toastType} 
            />
          )}
      </Layout>
    </BrowserRouter>
  );
}

export default App;